// pwa/src/components/ProductModalContent.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { useDropzone } from 'react-dropzone';

// MODIFICADO: Ahora acepta ambas props: shopId y productId
const ProductModalContent = React.forwardRef(({ shopId, productId, onSaveSuccess }, ref) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const textFieldRef = useRef(null);

  const [formData, setFormData] = useState({
    shopId: shopId,
    name: "",
    description: "",
    price: "",
    url: "",
  });
  const [imageFiles, setImageFiles] = useState([]); // local File objects
  const [existingImages, setExistingImages] = useState([]); // URLs already saved on server

  const onDrop = useCallback((acceptedFiles) => {
    // limit to images only; react-dropzone already filters by accept if configured
    setImageFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': []
    },
    maxFiles: 5,
  });

  React.useImperativeHandle(ref, () => ({
    handleClickOpen,
    handleClose,
  }));

  useEffect(() => {
    console.log("shopId in ProductModalContent:", shopId);
  }, [shopId]);


  // MODIFICADO useEffect: Maneja la carga de datos para edición y el reseteo para creación, basado en productId
  useEffect(() => {
    if (productId) {
      console.log(`Fetching product data for ID: ${productId}`);
      axios.get(process.env.REACT_APP_API_URL + `/api/products/${productId}`)
        .then(response => {
          const productData = response.data;
          setFormData({
            name: productData.name || "",
            description: productData.description || "",
            price: productData.price || "",
            url: productData.url || "",
          });
          // load existing images (URLs)
          setExistingImages(productData.images || []);
          setImageFiles([]);
          console.log("Product data loaded:", productData);
        })
        .catch(error => {
          console.error(`Error fetching product data for ID ${productId}:`, error);
          setFormData({
            shopId: shopId,
            name: "",
            description: "",
            price: "",
            url: "",
          });
        });
    } else {
      // Modo creación: Reiniciar el formulario
      console.log("Modal opened for new product creation. Resetting form.");
      setFormData({
        shopId: shopId,
        name: "",
        description: "",
        price: "",
        url: "",
      });
      setImageFiles([]);
      setExistingImages([]);
    }
  }, [productId]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log("Submitting form data:", formData);

    const apiEndpoint = productId
      ? process.env.REACT_APP_API_URL + `/api/products/${productId}` // Para actualización
      : process.env.REACT_APP_API_URL + "/api/products";            // Para creación

    // Use FormData when files are present
    let payload;
    let headers = {};
    if (imageFiles && imageFiles.length > 0) {
      payload = new FormData();
      payload.append('name', formData.name);
      payload.append('description', formData.description);
      payload.append('price', formData.price);
      payload.append('url', formData.url);
      imageFiles.forEach((f) => payload.append('images', f));
      headers['Content-Type'] = 'multipart/form-data';
    } else {
      // No files; send JSON body
      payload = formData;
    }

    const httpMethod = productId ? axios.put : axios.post; // Usar PUT para actualizar, POST para crear

    httpMethod(apiEndpoint, payload, { headers })
      .then((response) => {
        if (response.status === 200 || response.status === 201) { // 200 para PUT/OK, 201 para POST/Created
          console.log("Product operation successful:", response.data);
          handleClose();
          if (onSaveSuccess) {
            onSaveSuccess();
          }
        } else {
          console.log("API response status not successful:", response.status);
        }
      })
      .catch((error) => {
        console.log("handleSubmit product error: ");
        console.error(error);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      TransitionProps={{
        onEntered: () => {
          if (textFieldRef.current) {
            textFieldRef.current.focus();
          }
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>
          {/* Título dinámico: "Edit Product" o "Create Product" */}
          <FormattedMessage id={productId ? "app.products.edit.title" : "app.products.create.title"} />
        </span>
        <Box>
          <Tooltip title="Close">
            <IconButton aria-label="Close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form style={{ marginTop: "10px" }} onSubmit={handleSubmit}>
          <Grid
            container
            direction={"column"}
            spacing={2}
            className={"settingsContainer"}
          >
            <Grid item>
              <TextField
                name="name"
                id="outlined-basic-title"
                label="Titulo"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                inputRef={textFieldRef}
              />
            </Grid>
            <Grid item>
              <TextField
                name="description"
                id="outlined-multiline-static-description"
                label="Descripcion"
                multiline
                rows={4}
                fullWidth
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            <Grid item>
              <TextField
                name="price"
                id="outlined-basic-price"
                label="Price"
                fullWidth
                value={formData.price}
                onChange={handleChange}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item>
              <TextField
                name="url"
                id="outlined-basic-url"
                label="Link"
                fullWidth
                value={formData.url}
                onChange={handleChange}
                type="url"
              />
            </Grid>
            <Grid item>
              <Box
                {...getRootProps()}
                sx={{ border: '1px dashed #ccc', p: 2, textAlign: 'center', cursor: 'pointer' }}
              >
                <input {...getInputProps()} />
                <Typography variant="body2">Arrastra imágenes aquí o haz click para seleccionar (hasta 5)</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {existingImages.map((url, idx) => (
                  <Box key={`existing-${idx}`} sx={{ width: 80, height: 80, overflow: 'hidden', border: '1px solid #eee' }}>
                    <img src={process.env.REACT_APP_API_URL+url} alt={`img-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
                {imageFiles.map((file, idx) => (
                  <Box key={`file-${idx}`} sx={{ width: 80, height: 80, overflow: 'hidden', border: '1px solid #eee' }}>
                    <img src={URL.createObjectURL(file)} alt={`file-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} autoFocus>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ProductModalContent;